import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  ShieldCheck, 
  AlertCircle, 
  Clock, 
  CheckCircle, 
  Brain, 
  Edit3, 
  RefreshCw, 
  Save, 
  Eye, 
  User, 
  Heart, 
  Scale, 
  HeartHandshake,
  TrendingUp,
  Target,
  AlertTriangle,
  FileText
} from 'lucide-react';
import { cn } from '@/lib/utils';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerificationPortal = () => {
  const [pendingReviews, setPendingReviews] = useState([]);
  const [allTriplets, setAllTriplets] = useState([]);
  const [selectedTriplet, setSelectedTriplet] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [filterStatus, setFilterStatus] = useState('all');

  const industryIcons = {
    Medical: Heart,
    Legal: Scale,
    AgedCare: HeartHandshake
  };

  const industryColors = {
    Medical: '#ef4444',
    Legal: '#3b82f6',
    AgedCare: '#10b981'
  };

  const statusColors = {
    pending_review: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    staff_edited: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    delta_computed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch pending reviews
      const pendingResponse = await axios.get(`${API}/learning/pending-reviews`);
      setPendingReviews(pendingResponse.data || []);
      
      // Fetch all triplets
      const allResponse = await axios.get(`${API}/learning/triplets`);
      setAllTriplets(allResponse.data || []);
      
      setError(null);
    } catch (err) {
      console.error('Error fetching learning data:', err);
      setError('Failed to load learning data');
    } finally {
      setLoading(false);
    }
  };

  const handleTripletSelect = (triplet) => {
    setSelectedTriplet(triplet);
    // Initialize edited data with AI original summary
    if (triplet.ai_original_summary) {
      setEditedData({ ...triplet.ai_original_summary });
    }
  };

  const handleEditChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleResetToAI = () => {
    if (selectedTriplet?.ai_original_summary) {
      setEditedData({ ...selectedTriplet.ai_original_summary });
    }
  };

  const handleFinalize = async () => {
    if (!selectedTriplet) return;
    
    try {
      setSaving(true);
      
      await axios.post(`${API}/learning/triplets/${selectedTriplet.session_id}/staff-edit`, {
        staff_edit: editedData,
        editor_id: 'staff_user'
      });
      
      // Refresh data
      await fetchData();
      
      // Keep the same triplet selected but refresh its data
      const updatedTriplet = allTriplets.find(t => t.session_id === selectedTriplet.session_id);
      if (updatedTriplet) {
        setSelectedTriplet(updatedTriplet);
      }
      
    } catch (err) {
      console.error('Error saving staff edit:', err);
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const renderJsonField = (label, value, field, isEditable = true) => {
    if (typeof value === 'object' && value !== null) {
      return (
        <div className="space-y-2">
          <Label className="text-xs text-slate-400 uppercase tracking-wide">{label}</Label>
          {Object.entries(value).map(([key, val]) => (
            <div key={key} className="space-y-1">
              <Label className="text-xs text-slate-300 capitalize">{key.replace('_', ' ')}</Label>
              {isEditable ? (
                <Input
                  value={editedData[field]?.[key] || val || ''}
                  onChange={(e) => handleEditChange(field, {
                    ...editedData[field],
                    [key]: e.target.value
                  })}
                  className="bg-slate-800/50 border-slate-600 text-white text-sm"
                  placeholder={`Enter ${key}`}
                />
              ) : (
                <div className="bg-slate-800/30 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-300">
                  {String(val) || 'N/A'}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-1">
        <Label className="text-xs text-slate-400 uppercase tracking-wide">{label}</Label>
        {isEditable ? (
          <Textarea
            value={editedData[field] || value || ''}
            onChange={(e) => handleEditChange(field, e.target.value)}
            className="bg-slate-800/50 border-slate-600 text-white text-sm min-h-[60px]"
            placeholder={`Enter ${label.toLowerCase()}`}
          />
        ) : (
          <div className="bg-slate-800/30 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-300 min-h-[60px]">
            {String(value) || 'N/A'}
          </div>
        )}
      </div>
    );
  };

  const renderReasoningDelta = () => {
    if (!selectedTriplet?.reasoning_delta || selectedTriplet.status !== 'delta_computed') {
      return null;
    }

    const delta = selectedTriplet.reasoning_delta;

    return (
      <Card className="glass-card mt-6">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <Brain className="w-5 h-5 mr-2 text-cyan-400" />
            Reasoning Delta Analysis
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Accuracy Score */}
            {delta.accuracy && (
              <div className="space-y-2">
                <div className="text-sm text-slate-400">Accuracy Score</div>
                <div className="flex items-center space-x-3">
                  <div className="flex-1 bg-slate-700 rounded-full h-3 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400 transition-all duration-300"
                      style={{ width: `${delta.accuracy * 100}%` }}
                    />
                  </div>
                  <span className="text-white font-semibold">{Math.round(delta.accuracy * 100)}%</span>
                </div>
              </div>
            )}

            {/* Total Changes */}
            {delta.total_changes && (
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{delta.total_changes}</div>
                <div className="text-sm text-slate-400">Total Changes</div>
              </div>
            )}

            {/* Status */}
            <div className="text-center">
              <Badge className={statusColors[selectedTriplet.status] || 'bg-slate-500/20 text-slate-400'}>
                {selectedTriplet.status.replace('_', ' ')}
              </Badge>
            </div>
          </div>

          {/* Missed Details */}
          {delta.missed_details && delta.missed_details.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-red-400 mb-3 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Missed Details
              </h4>
              <div className="space-y-2">
                {delta.missed_details.map((detail, idx) => (
                  <div key={idx} className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                    <p className="text-sm text-red-400">{detail}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Improvement Suggestions */}
          {delta.improvement_suggestions && delta.improvement_suggestions.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-emerald-400 mb-3 flex items-center">
                <TrendingUp className="w-4 h-4 mr-2" />
                Improvement Suggestions
              </h4>
              <div className="space-y-2">
                {delta.improvement_suggestions.map((suggestion, idx) => (
                  <div key={idx} className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                    <p className="text-sm text-emerald-400">{suggestion}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const filteredTriplets = allTriplets.filter(triplet => {
    if (filterStatus === 'all') return true;
    return triplet.status === filterStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20">
          <ShieldCheck className="w-5 h-5 text-emerald-400" />
          <span className="text-white font-medium">Verification Portal</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white">
          Learning Loop <span className="bg-gradient-to-r from-emerald-400 to-cyan-500 bg-clip-text text-transparent">Management</span>
        </h1>
        <p className="text-sm sm:text-base text-slate-300">Review and refine AI assessments to improve future performance</p>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert className="bg-red-500/10 border-red-500/20 text-red-400">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* LEFT PANEL: Review Lists */}
        <div className="space-y-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 bg-slate-800/50">
              <TabsTrigger value="pending" className="text-xs">
                Pending ({pendingReviews.length})
              </TabsTrigger>
              <TabsTrigger value="all" className="text-xs">
                All Triplets
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending" className="space-y-3">
              <div className="text-sm font-medium text-slate-400 mb-3">Awaiting Review</div>
              {pendingReviews.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No pending reviews</p>
                </div>
              ) : (
                pendingReviews.map((triplet) => {
                  const IndustryIcon = industryIcons[triplet.industry_type] || FileText;
                  return (
                    <Card 
                      key={triplet.session_id} 
                      className={cn(
                        "glass-card cursor-pointer transition-all duration-200 hover:bg-slate-800/60",
                        selectedTriplet?.session_id === triplet.session_id && "ring-2 ring-emerald-500/50"
                      )}
                      onClick={() => handleTripletSelect(triplet)}
                    >
                      <CardContent className="p-3">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <IndustryIcon 
                                className="w-4 h-4" 
                                style={{ color: industryColors[triplet.industry_type] }} 
                              />
                              <span className="text-xs font-mono text-slate-300">
                                {triplet.session_id.slice(0, 8)}...
                              </span>
                            </div>
                            <Badge className={statusColors[triplet.status] || 'bg-slate-500/20 text-slate-400'}>
                              {triplet.status.split('_')[0]}
                            </Badge>
                          </div>
                          <div className="text-xs text-slate-400">
                            Triage: {triplet.triage_level || 'Unknown'}
                          </div>
                          <div className="flex items-center space-x-1 text-xs text-slate-500">
                            <Clock className="w-3 h-3" />
                            <span>{new Date(triplet.created_at || Date.now()).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </TabsContent>

            <TabsContent value="all" className="space-y-3">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-slate-400">All Learning Triplets</div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-32 bg-slate-800/50 border-slate-600 text-white text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending_review">Pending</SelectItem>
                      <SelectItem value="staff_edited">Edited</SelectItem>
                      <SelectItem value="delta_computed">Computed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="max-h-96 overflow-y-auto space-y-2">
                  {filteredTriplets.map((triplet) => {
                    const IndustryIcon = industryIcons[triplet.industry_type] || FileText;
                    return (
                      <Card 
                        key={triplet.session_id} 
                        className={cn(
                          "glass-card cursor-pointer transition-all duration-200 hover:bg-slate-800/60",
                          selectedTriplet?.session_id === triplet.session_id && "ring-2 ring-emerald-500/50"
                        )}
                        onClick={() => handleTripletSelect(triplet)}
                      >
                        <CardContent className="p-3">
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <IndustryIcon 
                                  className="w-4 h-4" 
                                  style={{ color: industryColors[triplet.industry_type] }} 
                                />
                                <span className="text-xs font-mono text-slate-300">
                                  {triplet.session_id.slice(0, 8)}...
                                </span>
                              </div>
                              <Badge className={statusColors[triplet.status] || 'bg-slate-500/20 text-slate-400'}>
                                {triplet.status.split('_')[0]}
                              </Badge>
                            </div>
                            <div className="text-xs text-slate-400">
                              Triage: {triplet.triage_level || 'Unknown'}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* RIGHT PANEL: Verification View */}
        <div className="lg:col-span-3">
          {selectedTriplet ? (
            <div className="space-y-6">
              {/* Session Info Bar */}
              <Card className="glass-card">
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                    <div>
                      <div className="text-xs text-slate-400 uppercase tracking-wide">Session ID</div>
                      <div className="text-sm font-mono text-white">{selectedTriplet.session_id}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 uppercase tracking-wide">Industry</div>
                      <div className="flex items-center space-x-2">
                        {React.createElement(industryIcons[selectedTriplet.industry_type] || FileText, {
                          className: "w-4 h-4",
                          style: { color: industryColors[selectedTriplet.industry_type] }
                        })}
                        <span className="text-sm text-white">{selectedTriplet.industry_type}</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 uppercase tracking-wide">Triage Level</div>
                      <Badge className={cn("text-xs",
                        selectedTriplet.triage_level === 'URGENT' ? 'bg-red-500/20 text-red-400' :
                        selectedTriplet.triage_level === 'HIGH' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-emerald-500/20 text-emerald-400'
                      )}>
                        {selectedTriplet.triage_level || 'NORMAL'}
                      </Badge>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 uppercase tracking-wide">Status</div>
                      <Badge className={statusColors[selectedTriplet.status] || 'bg-slate-500/20 text-slate-400'}>
                        {selectedTriplet.status.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Split View: AI Summary vs Staff Edit */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left: AI Original Summary (Read-only) */}
                <Card className="glass-card">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                      <Brain className="w-5 h-5 mr-2 text-cyan-400" />
                      AI Original Summary
                      <Badge className="ml-2 text-xs bg-cyan-500/20 text-cyan-400">Read Only</Badge>
                    </h3>
                    
                    {selectedTriplet.ai_original_summary ? (
                      <div className="space-y-4">
                        {Object.entries(selectedTriplet.ai_original_summary).map(([key, value]) => 
                          renderJsonField(key.replace('_', ' '), value, key, false)
                        )}
                      </div>
                    ) : (
                      <div className="text-slate-400 text-center py-8">
                        <Brain className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No AI summary available</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Right: Staff Edit Form (Editable) */}
                <Card className="glass-card">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                      <Edit3 className="w-5 h-5 mr-2 text-emerald-400" />
                      Staff Edit Form
                      <Badge className="ml-2 text-xs bg-emerald-500/20 text-emerald-400">Editable</Badge>
                    </h3>
                    
                    {selectedTriplet.ai_original_summary ? (
                      <div className="space-y-4">
                        {Object.entries(selectedTriplet.ai_original_summary).map(([key, value]) => 
                          renderJsonField(key.replace('_', ' '), value, key, true)
                        )}
                      </div>
                    ) : (
                      <div className="text-slate-400 text-center py-8">
                        <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No data to edit</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Action Bar */}
              <Card className="glass-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Button
                        onClick={handleResetToAI}
                        variant="outline"
                        size="sm"
                        className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        data-testid="reset-to-ai-button"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reset to AI
                      </Button>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Button
                        onClick={handleFinalize}
                        disabled={saving}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white"
                        data-testid="finalize-learn-button"
                      >
                        {saving ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Finalize & Learn
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Reasoning Delta Display */}
              {renderReasoningDelta()}
            </div>
          ) : (
            <Card className="glass-card">
              <CardContent className="p-12">
                <div className="text-center text-slate-400">
                  <ShieldCheck className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <h3 className="text-xl font-semibold text-white mb-2">Select a Learning Triplet</h3>
                  <p className="text-slate-400 mb-6">
                    Choose a session from the left panel to review and verify AI assessments
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md mx-auto">
                    <div className="text-left">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center mb-2">
                        <Eye className="w-4 h-4 text-emerald-400" />
                      </div>
                      <p className="text-sm text-white font-medium">Review AI Summaries</p>
                      <p className="text-xs text-slate-400">Compare original AI assessments with ground truth</p>
                    </div>
                    <div className="text-left">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center mb-2">
                        <Target className="w-4 h-4 text-emerald-400" />
                      </div>
                      <p className="text-sm text-white font-medium">Improve Accuracy</p>
                      <p className="text-xs text-slate-400">Edit and train the system for better performance</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerificationPortal;